import React from "react";
import { authApi } from "../api/auth";

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const refreshUser = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await authApi.me();
      setUser(data.user);
      return data.user;
    } catch (err) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async ({ email, password }) => {
    setError("");
    const data = await authApi.login({ email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    setError("");
    const data = await authApi.register(payload);

    // Solo dejamos sesión local si el backend creó una sesión real.
    // En el onboarding profesional, los usuarios normales quedan pending
    // y deben confirmar email + esperar aprobación WebProfit.
    const isActive = data?.user?.status === "active";
    const isSuperAdmin = data?.user?.globalRole === "superadmin" || data?.user?.isSuperAdmin;

    if (isActive && isSuperAdmin) {
      setUser(data.user);
    } else {
      setUser(null);
    }

    return data;
  };

  const logout = async () => {
    setError("");
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  };

  const value = React.useMemo(
    () => ({ user, loading, error, setError, login, register, logout, refreshUser }),
    [user, loading, error, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return context;
}
